import React, {ReactNode, useCallback, useEffect, useState} from 'react';
import DetailNavTitle from '@/components/business/detail-nav-title';
import {BasicObject, NavigatorScreenProps, SafeAny} from '@/types';
import globalStore from '@/services/global.state';
import Spin from '@/components/basic/spin';
import {ProgressiveCircularLoading} from '@fe-progressive/package-progressive/progressive-loading';
import theme from '@/style';
import {toGame} from '../game-navigate';
import {SectionList, View} from 'react-native';
import {
  GameListItem,
  LiveBannerItem,
  LiveHotGameItem,
  getBannersList,
  getCategories,
  getLiveHotGameList,
} from './live-casino-service';
import Text from '@/components/basic/text';
import LiveVideo from './live-casino-video';
import LiveCarousel from './live-casino-carousel';
// import LiveListItem from './live-casino-list-item';
// import {useScreenSize} from '@/common-pages/hooks/size.hooks';
import {useFocusEffect} from '@react-navigation/native';
import {goBack} from '@/utils';
// import HotList from '../hot-list';
import GameListWithHotList from './gamelist-with-hotlist';
import NoData from '@/components/basic/error-pages/no-data';
import {
  NewGameListParams,
  postNewGameList,
} from '@/common-pages/casino/casino.service';
import CasinoTabs from '@/pages/casino/casino-tabs';
import CasinoSelect from '@/pages/casino/casino-select';
const pageSize = 21;

function pairElements(arr: SafeAny[]) {
  const result = [];

  // 遍历数组，步长为2
  for (let i = 0; i < arr.length; i += 2) {
    // 取两个元素并组成一对
    const pair = [arr[i], i + 1 < arr.length ? arr[i + 1] : null];
    result.push(pair);
  }
  return result;
}

function nElements(arr: SafeAny[], n: number) {
  const result = [];
  let i = 0;
  while (arr.length > i * n) {
    result.push(arr.slice(i * n, ++i * n));
  }
  return result;
}

const LiveCasino = (
  props: NavigatorScreenProps & {renderAmount?: ReactNode},
) => {
  // const {calcActualSize} = useScreenSize();
  const {renderAmount} = props;
  const categoryId = 6;
  const [bannerLoading, setBannerLoading] = React.useState(false);
  const [listLoading, setListLoading] = React.useState(false);
  const [bannerList, setBannerList] = useState<LiveBannerItem[]>([]);
  const [hotGameList, setHotGameList] = useState<LiveHotGameItem[]>([]);
  const [gameList, setGameList] = useState<GameListItem[][]>([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [loadMore, setLoadMore] = useState<boolean>(false);
  const pageRef = React.useRef(1);
  const listRequestRef = React.useRef(0);
  const onEndReachedCalledDuringMomentum = React.useRef(true);
  const [hasMore, setHasMore] = React.useState(true);
  const firstLock = React.useRef(true);
  const [scrollY, setScrollY] = React.useState(0);
  const [headerHeight, setHeaderHeight] = React.useState(200);
  const [gameTypes, setGameTypes] = useState<{label: string; value: string}[]>(
    [],
  );
  const [gameType, setGameType] = useState('Lobby');
  const [provider, setProvider] = useState<string>('');
  const [sort, setSort] = useState<string>('');
  const handleGameTypeChange = (val: string) => {
    setGameType(val);
  };
  const handleChangeProvider = (value: string) => {
    setProvider(value);
  };

  const handleChangeSort = (value: string) => {
    setSort(value);
  };
  const initAmount = useCallback(() => {
    globalStore.token && globalStore.updateAmount.next({});
  }, []);
  useFocusEffect(initAmount);

  React.useEffect(() => {
    initGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    loadGameList(true, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, provider, gameType]);

  const loadGameList = async (refresh: boolean, old: boolean) => {
    let requestId = listRequestRef.current;
    try {
      if (!refresh && (listLoading || loadMore)) {
        return;
      }
      if (refresh) {
        requestId = ++listRequestRef.current;
        pageRef.current = 1;
        setListLoaded(false);
        setListLoading(true);
      } else {
        pageRef.current += 1;
        setLoadMore(true);
      }
      const params: NewGameListParams = {
        categoryId,
        gameType: gameType === 'Lobby' ? '' : gameType,
        // provider,
        providerId: provider,
        pageNo: pageRef.current,
        pageSize,
      };
      if (sort) {
        params.orderByField = 'name';
        params.isAsc = sort === 'A-Z';
      }
      const res = await postNewGameList(params);
      if (requestId === listRequestRef.current) {
        const content = res.content || [];
        const newArr = old ? pairElements(content) : nElements(content, 3);
        setGameList(current => (refresh ? newArr : current.concat(newArr)));
        setHasMore(pageRef.current < res.totalPages);
      }
    } catch {
      if (refresh && requestId === listRequestRef.current) {
        setGameList([]);
        setHasMore(false);
      }
    } finally {
      if (requestId === listRequestRef.current) {
        setListLoaded(true);
        setListLoading(false);
        setLoadMore(false);
      }
    }
  };

  const onLoadMore = async () => {
    if (hasMore && !onEndReachedCalledDuringMomentum.current) {
      await loadGameList(false, false);
      onEndReachedCalledDuringMomentum.current = true;
    }
  };
  const getGameCategories = () => {
    getCategories(categoryId)
      .then(res => {
        const values = Array.isArray(res)
          ? [
              ...new Set(
                res
                  .filter(value => typeof value === 'string')
                  .map(value => value.trim())
                  .filter(Boolean),
              ),
            ]
          : [];
        const gamesList = values.map(value => ({label: value, value}));
        setGameTypes([{label: 'Lobby', value: 'Lobby'}, ...gamesList]);
      })
      .catch(() => {
        setGameTypes([{label: 'Lobby', value: 'Lobby'}]);
      });
  };

  const initGames = async () => {
    setBannerLoading(true);
    getGameCategories();
    try {
      const [banner, hot] = await Promise.allSettled([
        getBannersList(),
        getLiveHotGameList(),
      ]);
      if (banner.status === 'fulfilled' && banner.value?.length > 0) {
        setBannerList(banner.value);
      } else {
        setBannerList([]);
      }
      setHotGameList(hot.status === 'fulfilled' ? hot.value : []);
    } finally {
      setBannerLoading(false);
      firstLock.current = false;
    }
  };

  const Footer = React.useMemo(() => {
    if (loadMore) {
      return (
        <View style={[theme.flex.center, theme.padding.l]}>
          <ProgressiveCircularLoading size={20} />
        </View>
      );
    }
    if (listLoaded && gameList.length === 0) {
      return (
        <View style={[theme.padding.xxl, theme.flex.center, theme.fill.fillW]}>
          <NoData />
        </View>
      );
    }
    if (gameList.length > 0 && !hasMore) {
      return (
        <View style={[theme.padding.btml]}>
          <Text textAlign="center" size="medium" color={'#9FA5AC'}>
            No more data
          </Text>
        </View>
      );
    }
    return null;
  }, [loadMore, listLoaded, gameList, hasMore]);

  // 兼容处理 videoUri
  const getMp4 = (item: LiveBannerItem) =>
    item.bannerImg.endsWith('mp4') ? item.bannerImg : item.otherParams;
  const videoUri = bannerList.length > 0 ? getMp4(bannerList[0]) : '';
  return (
    <View style={[theme.flex.flex1, theme.background.lightGrey]}>
      <DetailNavTitle
        // hideTitle={true}
        title={'Live'}
        hideAmount={!!renderAmount}
        rightNode={renderAmount}
        hideServer
        onBack={goBack}
        themeType="dark"
      />

      <Spin loading={bannerLoading || listLoading} style={[theme.flex.flex1]}>
        <View style={[theme.flex.flex1, theme.flex.basis0]}>
          <SectionList
            onScroll={e => {
              if (
                globalStore.isWeb &&
                onEndReachedCalledDuringMomentum.current
              ) {
                onEndReachedCalledDuringMomentum.current = false;
              }
              setScrollY(e.nativeEvent.contentOffset.y);
            }}
            // removeClippedSubviews={false}
            sections={[
              {
                title: '',
                data: gameList,
              },
            ]}
            // columnWrapperStyle={[{columnGap: 8}, theme.padding.lrl]}
            ListHeaderComponent={
              <View
                onLayout={e => setHeaderHeight(e.nativeEvent.layout.height)}>
                <LiveVideo
                  onPress={i => {
                    const current = bannerList[i];
                    toGame({
                      source: current.source,
                      id: current.gameId,
                      gameUrl: current.gameLink,
                      tripartiteUniqueness: current.tripartiteUniqueness,
                    });
                  }}
                  videoUri={videoUri}
                />
                <LiveCarousel
                  onPress={(info: BasicObject) =>
                    toGame({
                      source: info.source,
                      name: info.name || '',
                      id: info.gameId,
                      gameUrl: info.gameLink,
                      tripartiteUniqueness: info.tripartiteUniqueness,
                    })
                  }
                  bannerList={hotGameList}
                />
              </View>
            }
            stickySectionHeadersEnabled
            renderSectionHeader={({section: {}}) => (
              <div className="bg-white">
                <CasinoTabs
                  tabOptions={gameTypes}
                  value={gameType}
                  onChange={handleGameTypeChange}
                />

                <CasinoSelect
                  type="liveCasino"
                  handleChangeProvider={handleChangeProvider}
                  handleChangeSort={handleChangeSort}
                />
              </div>
            )}
            renderItem={({item, index}) => {
              // console.log('item', item);
              return (
                <GameListWithHotList
                  item={item}
                  isOld={false}
                  pageLoading={listLoading}
                  headerHeight={headerHeight}
                  scrollY={scrollY}
                  index={index}
                  gameList={gameList}
                  toGame={toGame}
                  theme={theme}
                />
              );
            }}
            keyExtractor={(item, index) => `${index}`}
            onEndReachedThreshold={0.2}
            onMomentumScrollBegin={() => {
              onEndReachedCalledDuringMomentum.current = false;
            }}
            onEndReached={onLoadMore}
            ListFooterComponent={Footer}
          />
        </View>
      </Spin>
    </View>
  );
};

export default LiveCasino;
